// Longform episodes are POSITIONAL (D-123, 2026-09-18).
//
// An episode's rows are the rows between its EPISODE BREAK markers: the first
// episode runs from the start of the body to the first break, the second from
// there to the next, and so on. Scene numbers restart at 1 after every break, so
// a scene number is unique only WITHIN an episode. Every consumer that wants "the
// rows of episode N" must therefore scope to episode N's positional block FIRST
// and only then look at scene numbers.
//
// Markers (the same shapes the parser emits, the grid inserts and from-breaks reads):
//   • CLOSE markers — "EPISODE BREAK", "END OF EPISODE …" — belong to the block
//     they close (the row carries the scene it closes); the next block starts
//     right after them.
//   • OPEN markers — "EPISODE n", "PART n", "CHAPTER n" headers — open the block
//     they head (the header is the block's first row). An open marker directly
//     after a close marker (or at the top) does not start a second, empty block.
// Blocks with no non-marker rows are dropped, so two breaks in a row, or a break
// as the very last row, never mint a phantom episode. Ep N ≡ the N-th block.
//
// Legacy tolerance: a body WITHOUT any marker has no positional information, and
// an episode number beyond the block count has no block — both fall back to the
// stored scene range over the whole body (the pre-D-123 behaviour), so bodies
// that carry continuous numbering keep working without a rewrite.
//
// This file is the ES-module twin of journey-viral-api/src/lib/longformEpisodes.js
// (only the export line differs). Change both together.

const BREAK_CLOSE_RE = /EPISODE\s*BREAK|END OF (?:EPISODE|EP\.?|PART|ACT)\b/i;
const BREAK_OPEN_RE  = /^\s*(?:EPISODE|EP\.?|PART|CHAPTER)\s+(?:[0-9]+|[IVXLC]+|ONE|TWO|THREE|FOUR|FIVE|SIX|SEVEN|EIGHT|NINE|TEN|ELEVEN|TWELVE)\b/i;

const sceneKey = (r) => String(r?.sceneNumber ?? '').trim();
const sceneNum = (r) => { const n = Number(sceneKey(r)); return Number.isFinite(n) && n > 0 && sceneKey(r) !== '' ? n : null; };
const isDirection = (r) => r?.kind === 'direction';
const isCloseMarker = (r) => isDirection(r) && BREAK_CLOSE_RE.test(String(r.line || ''));
const isOpenMarker  = (r) => isDirection(r) && !BREAK_CLOSE_RE.test(String(r.line || '')) && BREAK_OPEN_RE.test(String(r.line || ''));
const isBreakMarker = (r) => isCloseMarker(r) || isOpenMarker(r);

/**
 * episodeBlocks(rows) → [{ index, start, end, rowIndexes, sceneFrom, sceneTo, sceneNumbers, rowCount }]
 *   index       1-based episode ordinal (Ep N ≡ blocks[N-1])
 *   start/end   first row index / one past the last row index of the block (markers included)
 *   rowIndexes  every row of the block, markers included
 *   sceneFrom/To per-episode scene range read from the block's own NON-marker rows
 *               (marker rows only when the block has no numbered dialogue at all); null when unnumbered
 *   sceneNumbers the distinct scene numbers in the block, ascending
 *   rowCount    non-marker rows
 */
function episodeBlocks(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const raw = []; let cur = [];
  const flush = () => { if (cur.length) raw.push(cur); cur = []; };
  for (let i = 0; i < list.length; i++) {
    const r = list[i];
    if (isCloseMarker(r)) { cur.push(i); flush(); continue; }
    if (isOpenMarker(r)) { if (cur.some(j => !isBreakMarker(list[j]))) flush(); cur.push(i); continue; }
    cur.push(i);
  }
  flush();
  const blocks = raw.filter(idxs => idxs.some(j => !isBreakMarker(list[j])));
  return blocks.map((rowIndexes, k) => {
    const content = rowIndexes.filter(j => !isBreakMarker(list[j]));
    let nums = [...new Set(content.map(j => sceneNum(list[j])).filter(n => n != null))].sort((a, b) => a - b);
    if (!nums.length) nums = [...new Set(rowIndexes.map(j => sceneNum(list[j])).filter(n => n != null))].sort((a, b) => a - b);
    return { index: k + 1, start: rowIndexes[0], end: rowIndexes[rowIndexes.length - 1] + 1, rowIndexes,
      sceneFrom: nums.length ? nums[0] : null, sceneTo: nums.length ? nums[nums.length - 1] : null, sceneNumbers: nums, rowCount: content.length };
  });
}
/** True when the body carries at least one episode marker (positional information exists). */
function hasMarkers(rows) { return (Array.isArray(rows) ? rows : []).some(isBreakMarker); }
/** The positional block of episode N, or null (no markers, or N beyond the block count). */
function blockForEpisode(rows, episodeNumber) {
  const n = Number(episodeNumber);
  if (!Number.isInteger(n) || n < 1 || !hasMarkers(rows)) return null;
  return episodeBlocks(rows)[n - 1] || null;
}
/** Per-episode range label for Ep N from positions ("scenes 1–4"), or null when no block. */
function episodeRangeLabel(rows, episodeNumber) {
  const b = blockForEpisode(rows, episodeNumber);
  if (!b) return null;
  if (b.sceneFrom == null) return `${b.rowCount} row${b.rowCount === 1 ? '' : 's'}`;
  return `scenes ${b.sceneFrom}–${b.sceneTo}`;
}
/**
 * scopeBodyToEpisode(scriptBody, episodeNumber, sceneFrom, sceneTo)
 *   → { scriptBody, scope: 'positional' | 'range' | 'none', block }
 * positional: the body has markers and Ep N has a block → that block's non-marker rows (stored range ignored)
 * range:      no block → the stored inclusive range over the whole body (rows without a number are kept)
 * none:       neither → the body untouched
 */
function scopeBodyToEpisode(scriptBody, episodeNumber, sceneFrom, sceneTo) {
  if (!scriptBody || !Array.isArray(scriptBody.scenes)) return { scriptBody, scope: 'none', block: null };
  const rows = scriptBody.scenes;
  const block = blockForEpisode(rows, episodeNumber);
  if (block) {
    return { scriptBody: { ...scriptBody, scenes: block.rowIndexes.filter(j => !isBreakMarker(rows[j])).map(j => rows[j]) }, scope: 'positional', block };
  }
  const from = sceneFrom != null ? Number(sceneFrom) : null, to = sceneTo != null ? Number(sceneTo) : null;
  if (from == null && to == null) return { scriptBody, scope: 'none', block: null };
  const inRange = (row) => { const n = sceneNum(row); if (n == null) return true; if (from != null && n < from) return false; if (to != null && n > to) return false; return true; };
  return { scriptBody: { ...scriptBody, scenes: rows.filter(inRange) }, scope: 'range', block: null };
}
/**
 * The CURRENT episode for writing = the rows after the last close marker (or from
 * the last open marker). Empty when the body ends on a break — the next episode
 * has not been started yet.
 */
function currentEpisodeRows(rows) {
  const list = Array.isArray(rows) ? rows : [];
  let start = 0;
  for (let i = 0; i < list.length; i++) { if (isCloseMarker(list[i])) start = i + 1; else if (isOpenMarker(list[i])) start = i; }
  return list.slice(start).filter(r => !isBreakMarker(r));
}
/** "＋ Next scene" / Add scene prefill: the current episode's max scene + 1, or 1. */
function nextSceneNumber(rows) {
  const nums = currentEpisodeRows(rows).map(sceneNum).filter(n => n != null);
  return nums.length ? Math.max(...nums) + 1 : 1;
}
/**
 * The scene number for a row appended at the END of the body by a row-advance
 * (Enter / Ctrl+Enter / Tab-append / "Add scene row"): '1' right after a break —
 * the new episode's first line (D-124) — else the nearest numbered row above.
 */
function inheritedSceneForAppend(rows) {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return '1';
  if (isBreakMarker(list[list.length - 1])) return '1';
  const above = [...list].reverse().find(r => sceneKey(r) !== '');
  return above ? sceneKey(above) : '';
}

// ─── Episode titles live IN the script (D-126, 2026-09-18) ────────────────────
// An episode's title is carried by its header row — an open marker whose text is
// "EPISODE n — Title" (the shape the parser already recognises). No header → no
// title (the card defaults to "Episode N"). The number in the header is cosmetic;
// the block's ORDINAL is the episode number.
const HEADER_TITLE_RE = /^\s*(?:EPISODE|EP\.?|PART|CHAPTER)\s+(?:[0-9]+|[IVXLC]+|ONE|TWO|THREE|FOUR|FIVE|SIX|SEVEN|EIGHT|NINE|TEN|ELEVEN|TWELVE)\b\s*(?:[—–:\-.·|]+\s*)?(.*)$/i;
/** Title carried by an episode header row, or null (not a header / no title). */
function headerTitle(row) {
  if (!isOpenMarker(row)) return null;
  const m = String(row.line || '').match(HEADER_TITLE_RE);
  const t = m ? m[1].trim().replace(/^["“”']+|["“”']+$/g, '').trim() : '';
  return t || null;
}
/** The title of a block = its header row's title, or null. */
function blockTitle(rows, block) {
  const list = Array.isArray(rows) ? rows : [];
  if (!block) return null;
  return headerTitle(list[block.start]);
}
/** The header line "Add episode" / rename writes: "EPISODE n" or "EPISODE n — Title". */
function episodeHeaderLine(n, title) { const t = String(title || '').trim(); return `EPISODE ${n}${t ? ` — ${t}` : ''}`; }

// ─── Scene numbers are forced into succession per episode (D-130, 2026-09-18) ──
// Within an episode block, consecutive non-marker rows with the same Scene # form one
// scene; a change of Scene # starts the next. The scenes are then numbered 1, 2, 3…
// in order, so a typed "5" after scene 1 becomes scene 2 and a gap can never exist.
// Rows with no number join the scene above (or scene 1 at the top of an episode).
// Close markers keep "the scene they close"; header rows stay unnumbered.
// Returns { rows, changed } — rows is the same array when nothing changed.
function normalizeSceneNumbers(rows) {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return { rows: list, changed: false };
  const out = list.slice(); let changed = false;
  const set = (i, v) => { if (sceneKey(out[i]) !== v) { out[i] = { ...out[i], sceneNumber: v }; changed = true; } };
  const blocks = episodeBlocks(list);
  const covered = new Set();
  const walk = (indexes) => {
    let n = 0, prevKey = null, last = '';
    for (const i of indexes) {
      covered.add(i);
      const r = list[i];
      if (isOpenMarker(r)) { set(i, ''); continue; }
      if (isCloseMarker(r)) { set(i, last); continue; }
      const k = sceneKey(r);
      if (n === 0) { n = 1; prevKey = k; }
      else if (k !== '' && k !== prevKey) { n += 1; prevKey = k; }
      else if (k === '') { /* unnumbered row joins the scene above */ }
      last = String(n); set(i, last);
    }
  };
  for (const b of blocks) walk(b.rowIndexes);
  // rows outside every block (e.g. a body that is only markers) keep what they carry
  return { rows: changed ? out : list, changed };
}

// ─── Scenes of an episode, in order (D-132, 2026-09-18) ────────────────────────
// [{ ordinal, rowIndexes, docScene }] — one entry per contiguous run of one Scene # inside the
// block (marker rows excluded). docScene = the document's own scene number the run's first row
// was imported / pasted with (`_docScene`), or null for rows written in the editor.
function episodeScenes(rows, block) {
  const list = Array.isArray(rows) ? rows : [];
  if (!block) return [];
  const out = []; let prev = null;
  for (const i of block.rowIndexes) {
    const r = list[i]; if (isBreakMarker(r)) continue;
    const k = sceneKey(r);
    if (!out.length || (k !== '' && k !== prev)) { out.push({ ordinal: out.length + 1, rowIndexes: [i], docScene: r?._docScene != null && String(r._docScene).trim() !== '' ? String(r._docScene).trim() : null }); prev = k; }
    else out[out.length - 1].rowIndexes.push(i);
  }
  return out;
}

export { BREAK_CLOSE_RE, BREAK_OPEN_RE, isCloseMarker, isOpenMarker, isBreakMarker, episodeBlocks, hasMarkers, blockForEpisode, episodeRangeLabel, scopeBodyToEpisode, currentEpisodeRows, nextSceneNumber, inheritedSceneForAppend, sceneNum, HEADER_TITLE_RE, headerTitle, blockTitle, episodeHeaderLine, normalizeSceneNumbers, episodeScenes };
